import { NUGGET_SEVERITY, type NuggetSeverity } from "@/constants";

/**
 * Dot color per severity: red → orange → yellow.
 *
 * Uses dedicated `--theme-severity-*` tokens rather than error/warning/success so the three stay
 * mutually distinct and visually ordered in both light and dark themes.
 *
 * Not shared with `NuggetResultsList`'s local `SEVERITY_COLOR` (which colors `low` blue) or with
 * `LearningInsightCard`'s confidence map (where high is green because high confidence is good).
 * Those divergences are intentional — do not "unify" them.
 */
export const NUGGET_SEVERITY_COLOR: Record<NuggetSeverity, string> = {
  [NUGGET_SEVERITY.HIGH]: "var(--theme-severity-high)",
  [NUGGET_SEVERITY.MEDIUM]: "var(--theme-severity-medium)",
  [NUGGET_SEVERITY.LOW]: "var(--theme-severity-low)",
};

/** Most severe first — the order the detail modal's dropdown lists them in. */
export const NUGGET_SEVERITY_OPTIONS: readonly NuggetSeverity[] = [
  NUGGET_SEVERITY.HIGH,
  NUGGET_SEVERITY.MEDIUM,
  NUGGET_SEVERITY.LOW,
];

/**
 * `details.severity` is free-form on the wire: the authoring pipeline emits only "high"/"medium",
 * rows written before severity existed omit the key, and a manually uploaded nugget can hold any
 * string. Returns null for anything that is not one of the three known levels so callers render
 * "unset" rather than guessing — which is also why `isNuggetDetails` must keep accepting any string
 * for the field, since a strict guard there would silently drop whole nuggets in
 * `parseNuggetsResponse`.
 */
export function normalizeNuggetSeverity(value: unknown): NuggetSeverity | null {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  return (NUGGET_SEVERITY_OPTIONS as readonly string[]).includes(normalized)
    ? (normalized as NuggetSeverity)
    : null;
}
